import type { JourneyChoice } from "./journeyReader";

type Scope = JourneyChoice["scope"];
export type ChoiceReceipt = {
  schema: 1;
  scope: Scope;
  plannedVisitId: string;
  recommendationId: string;
  expectedVersion: number;
  idempotencyKey: string;
  state: "pending" | "unknown" | "blocked" | "received";
};
export type ReceiptRead = { kind: "empty" } | { kind: "held"; receipt: ChoiceReceipt } | { kind: "other" } | { kind: "error" };
const object = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === "object" && !Array.isArray(value);
const fields = (value: Record<string, unknown>, names: string[]) => Object.keys(value).length === names.length && names.every((name) => Object.hasOwn(value, name));
const identifier = (value: unknown): value is string => typeof value === "string" && value.length > 0 && value.length <= 256 && !/[\u0000-\u001f\u007f]/.test(value);
function validScope(value: unknown): value is Scope {
  if (!object(value) || !identifier(value.petId)) return false;
  return value.mode === "live"
    ? fields(value, ["mode", "userId", "householdId", "petId", "journeyId"]) && identifier(value.userId) && identifier(value.householdId) && identifier(value.journeyId)
    : value.mode === "fixture" && fields(value, ["mode", "petId", "scenario"]) && (value.scenario === undefined || identifier(value.scenario));
}
function subject(scope: Scope): string {
  return JSON.stringify(scope.mode === "live" ? ["live", scope.userId, scope.householdId, scope.petId, scope.journeyId] : ["fixture", scope.petId, scope.scenario ?? "default"]);
}
function storageKey(scope: Scope): string { return "petsoul:visit-choice:v1:" + subject(scope); }
function validReceipt(value: unknown): value is ChoiceReceipt {
  return object(value) && fields(value, ["schema", "scope", "plannedVisitId", "recommendationId", "expectedVersion", "idempotencyKey", "state"])
    && value.schema === 1 && validScope(value.scope) && identifier(value.plannedVisitId) && identifier(value.recommendationId)
    && Number.isSafeInteger(value.expectedVersion) && (value.expectedVersion as number) >= 0
    && typeof value.idempotencyKey === "string" && /^[A-Za-z0-9:._-]{1,128}$/.test(value.idempotencyKey)
    && ["pending", "unknown", "blocked", "received"].includes(value.state as string);
}
/** Same-tab continuation only. No epoch, payload, errors, token or expiry is serialized. */
export function readChoiceReceipt(scope: Scope, recommendationId: string, currentVisitId?: string): ReceiptRead {
  try {
    if (!validScope(scope) || !identifier(recommendationId)) return { kind: "error" };
    const raw = window.sessionStorage.getItem(storageKey(scope));
    if (raw === null) return { kind: "empty" };
    if (raw.length > 4096) return { kind: "error" };
    const value: unknown = JSON.parse(raw);
    if (!validReceipt(value) || subject(value.scope) !== subject(scope)) return { kind: "error" };
    // Only a server-confirmed old visit may give way to a genuinely different planned visit.
    if (value.recommendationId !== recommendationId && value.state === "received" && identifier(currentVisitId) && value.plannedVisitId !== currentVisitId) return { kind: "empty" };
    return value.recommendationId === recommendationId ? { kind: "held", receipt: value } : { kind: "other" };
  } catch { return { kind: "error" }; }
}
function persist(receipt: ChoiceReceipt): boolean {
  try {
    // JSON omits undefined; the fixture scope has its own explicit default namespace.
    const normalized = receipt.scope.mode === "fixture" ? { ...receipt, scope: { ...receipt.scope, scenario: receipt.scope.scenario ?? "default" } } : receipt;
    if (!validReceipt(normalized)) return false;
    const raw = JSON.stringify(normalized), key = storageKey(normalized.scope);
    window.sessionStorage.setItem(key, raw);
    return window.sessionStorage.getItem(key) === raw;
  } catch { return false; }
}
/** Synchronous reservation BEFORE a mutation is permitted to send its POST. */
export function reserveChoiceReceipt(capture: JourneyChoice, recommendationId: string): "reserved" | "held" | "error" {
  const prior = readChoiceReceipt(capture.scope, recommendationId, capture.plannedVisitId);
  if (prior.kind === "error") return "error";
  if (prior.kind !== "empty") return "held";
  return persist({ schema: 1, scope: capture.scope, plannedVisitId: capture.plannedVisitId, recommendationId, expectedVersion: capture.version, idempotencyKey: capture.idempotencyKey, state: "pending" }) ? "reserved" : "error";
}
/** Do not let an old response unlock a stopped/confirmed operation. No TTL or clear-and-resend. */
export function updateChoiceReceipt(capture: JourneyChoice, recommendationId: string, state: ChoiceReceipt["state"]): boolean {
  const prior = readChoiceReceipt(capture.scope, recommendationId);
  if (prior.kind !== "held") return false;
  const receipt = prior.receipt;
  if (receipt.idempotencyKey !== capture.idempotencyKey || receipt.plannedVisitId !== capture.plannedVisitId || receipt.expectedVersion !== capture.version) return false;
  const next = receipt.state === "received" || (receipt.state === "blocked" && state !== "received") ? receipt.state : state;
  // A minimal confirmed tombstone is a server fact, never a cached snapshot or authorization.
  return persist({ ...receipt, state: next });
}
